import { push } from '@lagunovsky/redux-react-router'
import { findIndex, maxBy, orderBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import Input from '../../../components/forms/Input'
import Select from '../../../components/forms/Select'
import { contentsPath } from '../../../conf/basepath'
import { PATH_ADMINISTRATION, PATH_ADMINISTRATION_CARTOGRAPHY } from '../../../home/constants/RouteConstants'
import StationAction from '../../../station/actions/StationAction'
import { hexToRGBColor, RGBToHexColor } from '../../../utils/ColorUtil'
import { setChoiceModal } from '../../../utils/FormUtils'
import { PROJECTIONS } from '../../../utils/mapUtils/CoordinateUtils'
import AdministrationAction from '../../actions/AdministrationAction'
import { LAYER_TYPES } from '../../constants/AdministrationConstants'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import ThemeDto from 'administration/dto/ThemeDto'
import { CardTitle } from 'components/card/NewCard'
import { Box, Button, Card, Divider, Grid2, IconButton, Typography } from '@mui/material'
import { Close, Queue } from '@mui/icons-material'
import VerticalSortableList from 'components/draggable/VerticalSortableList'
import { DragHandle, SortableItem } from 'components/draggable/components/sortableItems/SortableItem'
import { MAIN_RADIUS } from 'utils/constants/Theme'
import { favOrange, mainWhite } from 'utils/constants/ColorTheme'
import UndoIcon from '@mui/icons-material/Undo'
import AddBoxIcon from '@mui/icons-material/AddBox'
import LayerDto from 'home/dto/LayerDto'
import ProgressCard from 'components/card/ProgressCard'

const Layer = ({
    index,
    layer,
    onValidate = () => {},
}) => {
    const [defaultLayer, setDefaultLayer] = useState(layer)

    const iconButtonStyleIfHeader = index === 0 ? { marginTop: '28px' } : {}

    return (
        <Grid2
            container
            size='grow'
            columnSpacing={1.5}
            alignItems='center'
            sx={{
                '& .input-field': {
                    marginTop: '0.5rem !important',
                },
            }}
        >
            <Grid2>
                <Divider orientation='vertical' variant='middle' flexItem sx={{ height: '1.5rem' }} />
            </Grid2>
            <>
                <Grid2>
                    <IconButton
                        color='mainBlack'
                        onClick={() => {
                            onValidate(defaultLayer)
                        }}
                        sx={iconButtonStyleIfHeader}
                    >
                        <UndoIcon />
                    </IconButton>
                </Grid2>
            </>

            <Grid2 container size='grow' rowSpacing={0.5}>
                {index === 0 && (
                    <Grid2 container size={12} columnSpacing={1.75} alignItems='center'>
                        <Grid2 size='grow'>
                            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                                {i18n.name}*
                            </Typography>
                        </Grid2>
                        <Grid2 size='grow'>
                            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                                {i18n.type}*
                            </Typography>
                        </Grid2>
                        <Grid2 size='grow'>
                            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                                {i18n.url}*
                            </Typography>
                        </Grid2>
                        <Grid2 size='grow'>
                            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                                {i18n.code}
                            </Typography>
                        </Grid2>
                        <Grid2 size='grow'>
                            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                                {i18n.source}
                            </Typography>
                        </Grid2>
                        <Grid2 size='grow'>
                            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                                {i18n.projection}
                            </Typography>
                        </Grid2>
                    </Grid2>
                )}
                <Grid2 container size={12} columnSpacing={1.75} alignItems='center'>
                    <Grid2 size='grow'>
                        <Input
                            value={layer.name}
                            onChange={v => onValidate({ ...layer, name: v }) }
                        />
                    </Grid2>
                    <Grid2 size='grow'>
                        <Select
                            options={LAYER_TYPES}
                            onChange={v => onValidate({ ...layer, typeLayer: v }) }
                            value={layer.typeLayer}
                            keyvalue='code'
                        />
                    </Grid2>
                    <Grid2 size='grow'>
                        <Input
                            value={layer.url}
                            onChange={v => onValidate({ ...layer, url: v }) }
                        />
                    </Grid2>
                    <Grid2 size='grow'>
                        <Input
                            value={layer.layer}
                            onChange={v => onValidate({ ...layer, layer: v }) }
                        />
                    </Grid2>
                    <Grid2 size='grow'>
                        <Input
                            value={layer.source}
                            onChange={v => onValidate({ ...layer, source: v }) }
                        />
                    </Grid2>
                    <Grid2 size='grow'>
                        <Select
                            value={layer.projection}
                            options={PROJECTIONS}
                            onChange={v => onValidate({ ...layer, projection: v }) }
                            keyvalue='code'
                        />
                    </Grid2>
                </Grid2>
            </Grid2>
        </Grid2>
    )
}

Layer.propTypes = {
    index: PropTypes.number,
    layer: PropTypes.instanceOf(LayerDto).isRequired,
    onValidate: PropTypes.func,
}

const ThemeApp = ({}) => {
    const {
        accountUser,
        theme,
        layers,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        theme: store.AdministrationReducer.theme,
        layers: store.AdministrationReducer.layers,
    }))

    const [documentLoading, setDocumentLoading] = useState(false)
    const [newLayers, setNewLayers] = useState(orderBy(layers, 'order', 'desc'))
    const [newTheme, setNewTheme] = useState({
        ...new ThemeDto(theme),
        color: RGBToHexColor(theme.color),
    })
    const [isLoading, setIsLoading] = useState(true)

    const params = useParams()

    const dispatch = useDispatch()

    useTitle(() => [{
        title: i18n.administration,
        href: PATH_ADMINISTRATION,
    },
    {
        title: i18n.cartography,
        href: `${PATH_ADMINISTRATION}/${PATH_ADMINISTRATION_CARTOGRAPHY}`,
    },
    {
        title: params.id === 'new' ? i18n.newTheme : (newTheme?.name || newTheme?.id || ''),
        href: `${PATH_ADMINISTRATION}/${PATH_ADMINISTRATION_CARTOGRAPHY}/${params.id}`,
    }], [params, newTheme])

    const layersNoUrlWarning = newLayers.some(({ url }) => !url)

    const handleNewTheme = receivedTheme => {
        setNewTheme({
            ...new ThemeDto(receivedTheme),
            color: RGBToHexColor(receivedTheme.color),
        })
    }

    const handleNewLayers = receivedLayers => {
        setIsLoading(false)
        setNewLayers(orderBy(receivedLayers.map(receivedLayer => new LayerDto(receivedLayer)), 'ordre', 'desc'))
    }

    // the first item has to have the higher order for openlayer
    const handleOrderLayers = unorderedLayers => unorderedLayers.map((layer, i) => (new LayerDto({ ...layer, ordre: unorderedLayers.length - 1 - i })))

    const handleSave = () => {
        const formatedTheme = {
            ...newTheme,
            color: hexToRGBColor(newTheme.color),
            layers: handleOrderLayers(newLayers),
        }
        if (layersNoUrlWarning) {
            dispatch(ToastrAction.warning(i18n.warningNoUrlLayer))
        } else if (params.id !== 'new') {
            setIsLoading(true)
            dispatch(AdministrationAction.updateTheme(params.id, formatedTheme)).then(() => dispatch(AdministrationAction.fetchTheme(params.id, handleNewTheme, handleNewLayers)))
        } else {
            setIsLoading(true)
            dispatch(AdministrationAction.createTheme(formatedTheme)).then((id) =>
                dispatch(AdministrationAction.fetchTheme(id, handleNewTheme, handleNewLayers)).then(() =>
                    dispatch(push(`/administration/cartography/${id}`)),
                ),
            )
        }
    }

    useActions(() => {
        const defaultActions = { cancel: () => dispatch(push('/administration/cartography')) }
        if (accountUser.consultant === '1') {
            return defaultActions
        }

        const deleteAction = params.id !== 'new' ? { delete: () => dispatch(AdministrationAction.deleteTheme(params.id)) } : {}
        return {
            save: handleSave,
            ...defaultActions,
            ...deleteAction,
        }
    }, [params, newTheme, newLayers])

    useEffect(() => {
        if (params.id !== 'new') {
            dispatch(AdministrationAction.fetchTheme(params.id, handleNewTheme, handleNewLayers))
        } else {
            setNewLayers([])
            setIsLoading(false)
        }

        return () => dispatch(AdministrationAction.resetTheme())
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChangeLayers = layer => {
        const layerIndex = findIndex(newLayers, nl => nl.id === layer.id)
        setNewLayers([...newLayers.slice(0, layerIndex), layer, ...newLayers.slice(layerIndex + 1)])
    }

    const handleDuplicateLayer = layer => {
        const layerIndex = findIndex(newLayers, nl => nl.id === layer.id)
        const orderedLayers = handleOrderLayers([...newLayers.slice(0, layerIndex), layer, new LayerDto({ ...layer, id: (maxBy(newLayers, 'id').id + 1) }), ...newLayers.slice(layerIndex + 1)])
        setNewLayers(orderedLayers)
    }

    const handleDeleteLayer = layer => {
        const layersToKeep = newLayers.filter(({ id }) => id !== layer.id)
        setNewLayers(handleOrderLayers(layersToKeep))

        if (layer.url?.includes(contentsPath)) {
            setChoiceModal(i18n.wantToDeleteLayerFile, {
                yes: () => {
                    const fileToDelete = {
                        name: layer.url.split(`${contentsPath}CARTO`)[1],
                        fileType: 'carto',
                    }
                    dispatch(StationAction.deleteFile(fileToDelete))
                },
            })
        }
    }

    const addParameter = () => {
        setNewLayers([
            new LayerDto({
                id: ((maxBy(newLayers, 'id')?.id ?? 0) + 1),
                theme: newTheme.id,
            }),
            ...newLayers,
        ])
    }

    const saveKML = (file, name) => {
        dispatch(AdministrationAction.uploadKMLFileTheme(file)).then(() => {
            setNewLayers(handleOrderLayers([
                new LayerDto({
                    name: file.name,
                    typeLayer: 1,
                    url: `${contentsPath}CARTO/${file.name}`,
                    layer: name,
                }),
                ...newLayers,
            ]))
            setDocumentLoading(false)
        })
    }

    const onUploadKML = (event) => {
        if (event.target.files && event.target.files[0]) {
            setDocumentLoading(true)
            let reader = new FileReader()
            const file = event.target.files[0]
            const name = file.name.replace(/ /g, '_')
            reader.onload = (e) => {
                saveKML({
                    stationType: -1,
                    stationId: -1,
                    path: '',
                    name: name.toUpperCase(),
                    content: e.target.result,
                }, file.name)
            }
            reader.readAsDataURL(event.target.files[0])
        }
    }

    if (isLoading) {
        return <ProgressCard indeterminate />
    }

    return (
        <Box sx={{ padding: '1rem' }}>
            <Card>
                <Grid2 container direction='column' rowSpacing={1.5} sx={{ padding: '1rem' }}>
                    <Grid2 container justifyContent='space-between' alignItems='center'>
                        <Grid2 container size={3.5} columnSpacing={1.5} alignItems='start'>
                            <Grid2 size='grow'>
                                <Input
                                    title={i18n.name}
                                    value={newTheme.name}
                                    onChange={v => setNewTheme({ ...newTheme, name: v })}
                                />
                            </Grid2>
                            <Grid2>
                                <label htmlFor='title-color' >{ i18n.color }</label>
                                <input
                                    type='color'
                                    className='sieau-input form-control input-sm '
                                    data-attribute='color'
                                    id='title-color'
                                    value={ newTheme.color }
                                    onChange={v => setNewTheme({ ...newTheme, color: v.target.value })}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container size={2.5} columnSpacing={1.5} alignItems='center'>
                            <Grid2 size={6}>
                                <Button
                                    variant='contained'
                                    startIcon={<AddBoxIcon />}
                                    onClick={addParameter}
                                    fullWidth
                                >
                                    {i18n.add}
                                </Button>
                            </Grid2>
                            <Grid2 size={6}>
                                <Button
                                    component='label'
                                    variant='contained'
                                    tabIndex={-1}
                                    loading={documentLoading}
                                    fullWidth
                                >
                                    {i18n.importLabel}
                                    <Box
                                        component='input'
                                        type='file'
                                        onChange={onUploadKML}
                                        multiple
                                        sx={{
                                            clip: 'rect(0 0 0 0)',
                                            clipPath: 'inset(50%)',
                                            height: 1,
                                            overflow: 'hidden',
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            whiteSpace: 'nowrap',
                                            width: 1,
                                        }}
                                    />
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                    {layersNoUrlWarning && (
                        <Grid2>
                            <Box sx={{
                                backgroundColor: favOrange,
                                borderRadius: MAIN_RADIUS,
                                padding: '0.25rem 0.5rem',
                                margin: '10px 0',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                color: mainWhite,
                            }}
                            >
                                {i18n.warningNoUrlLayer}
                            </Box>
                        </Grid2>
                    )}
                    {!!newLayers.length && (
                        <Grid2 sx={{ '& svg:hover': { cursor: 'pointer' } }}>
                            <Card>
                                <CardTitle title={i18n.orderLayers} />
                                <VerticalSortableList
                                    items={newLayers}
                                    onChange={setNewLayers}
                                    renderItem={(layer, index) => (
                                        <SortableItem id={layer.id}>
                                            <Grid2 container size={12} columnSpacing={1.5} alignItems={index === 0 ? 'baseline' : 'center'}>
                                                <Grid2>
                                                    <IconButton
                                                        color='mainBlack'
                                                        onClick={() => handleDuplicateLayer(layer)}
                                                    >
                                                        <Queue />
                                                    </IconButton>
                                                </Grid2>
                                                <Grid2>
                                                    <IconButton
                                                        color='mainBlack'
                                                        onClick={() => handleDeleteLayer(layer)}
                                                    >
                                                        <Close />
                                                    </IconButton>
                                                </Grid2>
                                                <Layer
                                                    index={index}
                                                    layer={layer}
                                                    onValidate={handleChangeLayers}
                                                />
                                                <Grid2>
                                                    <DragHandle />
                                                </Grid2>
                                            </Grid2>
                                        </SortableItem>
                                    )}
                                />
                            </Card>
                        </Grid2>
                    )}
                    <Grid2>{i18n.keyWordNOW}</Grid2>
                </Grid2>
            </Card>
        </Box>
    )
}

export default ThemeApp
